import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getMe, isAdmin, isCompany, isStaff } from './features/auth/authSlice';
import { setTheme } from './features/ui/uiSlice';
import Layout from './components/Layout/Layout';
import ScrollToTop from './components/ScrollToTop';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import Profile from './pages/Profile/Profile';
import Tasks from './pages/Jobs/TaskManagement';
import Notifications from './pages/Notifications/Notifications';
import AdminPanel from './pages/Admin/AdminPanel';
import Users from './pages/Admin/Users';
import StaffManagement from './pages/Admin/StaffManagement';
import Companies from './pages/Admin/Companies';
import AdminTasks from './pages/Admin/Tasks';
import EditTask from './pages/Admin/EditTask';
import AdminTaskDetails from './pages/Admin/TaskDetails';
import AdminBankVerification from './pages/Admin/AdminBankVerification';
import AdminWithdrawalManagement from './pages/Admin/AdminWithdrawalManagement';
import AdminEarnings from './pages/Admin/AdminEarnings';
import Inquiries from './pages/Admin/Inquiries';
import Analytics from './pages/Admin/Analytics';
import Reports from './pages/Admin/Reports';
import Skills from './pages/Admin/Skills';
import CompanyDashboard from './pages/Company/CompanyDashboard';
import CompanyTeam from './pages/Company/CompanyTeam';
import CompanyTasks from './pages/Company/CompanyTasks';
import CompanyTaskDetails from './pages/Company/CompanyTaskDetails';
import CompanyAnalytics from './pages/Company/CompanyAnalytics';
import CompanyProfile from './pages/Company/CompanyProfile';
import LandingPage from './pages/Landing/LandingPage';
import About from './pages/Landing/About';
import Services from './pages/Landing/Services';
import Projects from './pages/Landing/Projects';
import Contact from './pages/Landing/Contact';
import Career from './pages/Landing/Career';
import Privacy from './pages/Landing/Privacy';
import FAQ from './pages/Landing/FAQ';
import RefundPolicy from './pages/Landing/RefundPolicy';
import TermsOfUse from './pages/Landing/TermsOfUse';
import Blog from './pages/Landing/Blog';
import BlogDetails from './pages/Landing/BlogDetails';
import ProjectDetails from './pages/Landing/ProjectDetails';
import Partners from './pages/Landing/Partners';
import ScreensDashboard from './pages/Screens/ScreensDashboard';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Import new components to make TypeScript recognize them
import AdminCompanyInquiryManagement from './components/AdminCompanyInquiryManagement';
import CompanyInquiryForm from './components/CompanyInquiryForm';
import TaskForm from './components/TaskForm';
import TaskApplications from './pages/User/TaskApplications';
import TaskApplicationManagement from './pages/Admin/TaskApplicationManagement';
import AdminTaskApplications from './pages/Admin/AdminTaskApplications';
import TaskHierarchyView from './components/TaskHierarchyView';
import WalletManagement from './components/WalletManagement';
import UserTaskDetails from './pages/User/UserTaskDetails';
import { StaffDashboard, RecruiterManagement, BDMManagement, TrainerManagement, CRMManagement } from './pages/Staff';
import CustomerSupport from './pages/CustomerSupport/CustomerSupport';
import ProjectsManagement from './pages/Admin/ProjectsManagement';
import SlidersManagement from './pages/Admin/SlidersManagement';
import ContactsManagement from './pages/Admin/ContactsManagement';
import BlogsManagement from './pages/Admin/BlogsManagement';
import PartnersManagement from './pages/Admin/PartnersManagement';

// Wrapper component for TaskHierarchyView to handle route params
const TaskHierarchyViewWrapper: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  return <TaskHierarchyView taskId={taskId || ''} />;
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, isLoading, user } = useAppSelector((state) => state.auth);
  const getMeCalled = useRef(false);

  useEffect(() => {
    const darkMode = localStorage.getItem('darkMode') === 'true';
    const theme = localStorage.getItem('theme') as 'light' | 'dark' || (darkMode ? 'dark' : 'light');
    dispatch(setTheme(theme));
  }, [dispatch]);

  useEffect(() => {
    // Check if we have a token but no user data, and we're not currently loading
    const storedToken = localStorage.getItem('token');
    if (storedToken && !user && !isLoading && !getMeCalled.current) {
      getMeCalled.current = true;
      dispatch(getMe()).finally(() => {
        getMeCalled.current = false;
      });
    }
  }, [dispatch, token, user, isLoading]);

  // Show loading only when explicitly loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="spinner"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ScrollToTop />
      <Routes>
        {/* Landing page - only accessible when not logged in */}
        <Route path="/" element={user ? <Navigate to={
          isAdmin(user) ? "/admin" :
            isCompany(user) ? "/company" :
              isStaff(user) ? "/staff" :
                "/dashboard"
        } /> : <LandingPage />} />

        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to={
          isAdmin(user) ? "/admin" :
            isCompany(user) ? "/company" :
              isStaff(user) ? "/staff" :
                "/dashboard"
        } /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={
          isAdmin(user) ? "/admin" :
            isCompany(user) ? "/company" :
              isStaff(user) ? "/staff" :
                "/dashboard"
        } /> : <Register />} />
        <Route path="/forgot-password" element={user ? <Navigate to={
          isAdmin(user) ? "/admin" :
            isCompany(user) ? "/company" :
              isStaff(user) ? "/staff" :
                "/dashboard"
        } /> : <ForgotPassword />} />
        <Route path="/reset-password" element={user ? <Navigate to={
          isAdmin(user) ? "/admin" :
            isCompany(user) ? "/company" :
              isStaff(user) ? "/staff" :
                "/dashboard"
        } /> : <ResetPassword />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:slug" element={<ProjectDetails />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/career" element={<Career />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetails />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/company-inquiry" element={<CompanyInquiryForm />} />

        {/* Protected routes - only accessible when logged in */}
        <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />

          {/* Task management routes */}
          <Route path="tasks" element={<Tasks />} />
          <Route path="tasks/:taskId" element={<UserTaskDetails />} />
          <Route path="tasks/create" element={<TaskForm />} />
          <Route path="tasks/hierarchy/:taskId" element={<TaskHierarchyViewWrapper />} />

          {/* Wallet management routes */}
          <Route path="applications" element={<TaskApplications />} />
          <Route path="wallet" element={<WalletManagement />} />

          {/* Notification routes */}
          <Route path="notifications" element={<Notifications />} />

          {/* Screen management routes */}
          <Route path="screens" element={<ScreensDashboard />} />
        </Route>

        {/* Admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRole="admin">
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminPanel />} />
          <Route path="users" element={<Users />} />
          <Route path="staff" element={<StaffManagement />} />
          <Route path="companies" element={<Companies />} />
          <Route path="tasks" element={<AdminTasks />} />
          <Route path="tasks/:id" element={<AdminTaskDetails />} />
          <Route path="tasks/:id/applications" element={<TaskApplicationManagement />} />
          <Route path="tasks/:id/edit" element={<EditTask />} />
          <Route path="bank-verification" element={<AdminBankVerification />} />
          <Route path="withdrawals" element={<AdminWithdrawalManagement />} />
          <Route path="earnings" element={<AdminEarnings />} />
          <Route path="task-applications" element={<AdminTaskApplications />} />
          <Route path="inquiries" element={<Inquiries />} />
          <Route path="company-inquiries" element={<AdminCompanyInquiryManagement />} />
          <Route path="skills" element={<Skills />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="reports" element={<Reports />} />
          <Route path="projects" element={<ProjectsManagement />} />
          <Route path="sliders" element={<SlidersManagement />} />
          <Route path="contacts" element={<ContactsManagement />} />
          <Route path="blogs" element={<BlogsManagement />} />
          <Route path="partners" element={<PartnersManagement />} />
        </Route>

        {/* Company routes */}
        <Route path="/company" element={
          <ProtectedRoute requiredRole="company">
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<CompanyDashboard />} />
          <Route path="team" element={<CompanyTeam />} />
          <Route path="tasks" element={<CompanyTasks />} />
          <Route path="tasks/:id" element={<CompanyTaskDetails />} />
          <Route path="analytics" element={<CompanyAnalytics />} />
          <Route path="profile" element={<CompanyProfile />} />
        </Route>

        {/* Staff routes */}
        <Route path="/staff" element={
          <ProtectedRoute requiredRoles={['recruiter', 'bdm', 'project_trainer', 'crm']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<StaffDashboard />} />
          <Route path="recruiter" element={<RecruiterManagement />} />
          <Route path="bdm" element={<BDMManagement />} />
          <Route path="trainer" element={<TrainerManagement />} />
          <Route path="crm" element={<CRMManagement />} />

          {/* Staff access to admin pages */}
          <Route path="users" element={<Users />} />
          <Route path="staff-management" element={<StaffManagement />} />
          <Route path="task-applications" element={<AdminTaskApplications />} />
          <Route path="task-applications/:id" element={<TaskApplicationManagement />} />
          <Route path="data-validation" element={<AdminTasks />} />
          <Route path="assigned-tasks" element={<AdminTasks />} />
          <Route path="inquiries" element={<AdminCompanyInquiryManagement />} />
          <Route path="customer-support" element={<Inquiries />} />
          <Route path="tasks" element={<AdminTasks />} />
          <Route path="tasks/:id" element={<AdminTaskDetails />} />
          <Route path="tasks/:id/applications" element={<TaskApplicationManagement />} />
          <Route path="tasks/:id/edit" element={<EditTask />} />
        </Route>

        {/* Customer Support routes */}
        <Route path="/customer-support" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<CustomerSupport />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

export default App;